import { useReducer, useEffect, } from 'react';
import { useMountedState, } from 'react-use';
import {
    collection,
    doc,
    addDoc,
    updateDoc,
    deleteDoc,
    deleteField,
    arrayUnion,
    arrayRemove,
    Timestamp, } from 'firebase/firestore';
import { db, } from '../firebase/config';
import { useSnackbar, } from 'notistack';

const firestoreReducer = (state, action) => {
    switch (action.type) {
        case 'IS_PENDING':
            return { document: null, isPending: true, success: false, error: null, };

        case 'ADDED_DOCUMENT':
            return { document: action.payload, isPending: false, success: true, error: null, };

        case 'UPDATED_DOCUMENT':
            return { document: null, isPending: false, success: true, error: null, };

        case 'UPDATED_ARRAY_DOCUMENT':
            return { document: action.payload, isPending: false, success: true, error: null, };

        case 'DELETED_DOCUMENT':
            return { document: null, isPending: false, success: true, error: null, };

        case 'DELETED_FIELD_DOCUMENT':
            return { document: action.payload, isPending: false, success: true, error: null, };

        case 'ERROR':
            return { document: null, isPending: false, success: false, error: action.payload, };

        default:
            return state;
    }
};

export const useFirestore = (col) => {
    const [response, dispatch] = useReducer(firestoreReducer, {
        document: null,
        isPending: false,
        success: null,
        error: null,
    });
    const { enqueueSnackbar, } = useSnackbar();
    const isMounted = useMountedState();

    const snackbarFirestoreError = (variant, message) => () => {
        enqueueSnackbar(message, { variant });
    };

    const ref = collection(db, col);

    const dispatchIfMounted = (action) => {
        if (isMounted()) {
            dispatch(action);
        }
    };

    const addDocument = async (doc) => {
        dispatch({ type: 'IS_PENDING' });

        try {
            const createdAt = Timestamp.fromDate(new Date());
            const addedDocument = await addDoc(ref, { ...doc, createdAt, });

            dispatchIfMounted({ type: 'ADDED_DOCUMENT', payload: addedDocument, });

            return addedDocument;
        } catch (error) {
            dispatchIfMounted({ type: 'ERROR', payload: error, });

            return false;
        }
    };

    const updateDocument = async (id, updates) => {
        dispatch({ type: 'IS_PENDING' });

        try {
            const updatedDocumentRef = doc(db, col, id);
            await updateDoc(updatedDocumentRef, updates);

            dispatchIfMounted({ type: 'UPDATED_DOCUMENT', });

            return true;
        } catch (error) {
            dispatchIfMounted({ type: 'ERROR', payload: error, });

            return false;
        }
    };

    const updateArrayDocument = async (documentId, id, fieldName, action) => {
        dispatch({ type: 'IS_PENDING', });

        try {
            let updatedDocument;
            const updatedArrayDocumentRef = doc(db, col, documentId);

            if (action === 'add') {
                updatedDocument = await updateDoc(updatedArrayDocumentRef, {
                    [fieldName]: arrayUnion(id),
                });
            } else if (action === 'delete') {
                updatedDocument = await updateDoc(updatedArrayDocumentRef, {
                    [fieldName]: arrayRemove(id),
                });
            }

            dispatchIfMounted({ type: 'UPDATED_ARRAY_DOCUMENT', payload: updatedDocument, });

            return true;
        } catch (error) {
            dispatchIfMounted({ type: 'ERROR', payload: error, });

            return false;
        }
    };

    const deleteDocument = async (id) => {
        dispatch({ type: 'IS_PENDING', });

        try {
            const deletedDocumentRef = doc(db, col, id);
            await deleteDoc(deletedDocumentRef);
            dispatchIfMounted({ type: 'DELETED_DOCUMENT', });

            return true;
        } catch (error) {
            dispatchIfMounted({ type: 'ERROR', payload: error, });

            return false;
        }
    };

    const deleteFieldDocument = async (id, fieldName) => {
        dispatch({ type: 'IS_PENDING', });

        try {
            const deletedFieldDocumentRef = doc(db, col, id);

            const updatedDocument = await updateDoc(deletedFieldDocumentRef, {
                [fieldName]: deleteField(),
            });
            dispatchIfMounted({ type: 'DELETED_FIELD_DOCUMENT', payload: updatedDocument, });

            return true;
        } catch (error) {
            dispatchIfMounted({ type: 'ERROR', payload: error, });

            return false;
        }
    };

    useEffect(() => {
        response.error && snackbarFirestoreError('error', `Firebase: ${response.error.message}`)();
    }, [response.error]);

    return {
        response,
        addDocument,
        updateDocument,
        updateArrayDocument,
        deleteDocument,
        deleteFieldDocument,
    };
};